import type { TFunction } from "i18next";
import { formatDateShort } from "./formatUtils";

/**
 * Converts a timestamp to a relative time string (e.g., "5m", "2h", "3d")
 * Uses i18n for localized suffixes
 */
export const formatTimeAgo = (timestamp: string, t: TFunction): string => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));

  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${t("beneficiary_detail.activity.minutes")} ${t("beneficiary_detail.activity.ago")}`;
  } else if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60);
    return `${hours} ${t("beneficiary_detail.activity.hours")} ${t("beneficiary_detail.activity.ago")}`;
  } else {
    const days = Math.floor(diffInMinutes / 1440);
    return `${days} ${t("beneficiary_detail.activity.days")} ${t("beneficiary_detail.activity.ago")}`;
  }
};

export const getRelativeTime = (timestamp: string, t: TFunction, locale?: string): string => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffInMinutes < 1) return t("notifications.just_now");
  if (diffInMinutes < 60) return `${diffInMinutes}${t("notifications.minutes_ago")}`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}${t("notifications.hours_ago")}`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}${t("notifications.days_ago")}`;

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) return `${diffInWeeks}${t("notifications.weeks_ago", "sem")}`;

  return date.toLocaleDateString(locale);
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const formatCompactRelative = (dateStr: string, t: TFunction): string => {
  const elapsed = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(elapsed / MINUTE_MS);
  if (minutes < 1) return t("common.relative.now");
  if (minutes < 60) return t("common.relative.minutes", { count: minutes });
  const hours = Math.floor(elapsed / HOUR_MS);
  if (hours < 24) return t("common.relative.hours", { count: hours });
  const days = Math.floor(elapsed / DAY_MS);
  if (days === 1) return t("common.relative.yesterday");
  if (days < 7) return t("common.relative.days", { count: days });
  if (days < 30) return t("common.relative.weeks", { count: Math.floor(days / 7) });
  if (days < 365) return t("common.relative.months", { count: Math.floor(days / 30) });
  return formatDateShort(dateStr);
};

export const formatTemplateRelativeDate = (
  dateStr: string | null | undefined,
  t: TFunction,
): string => {
  if (!dateStr) return "";
  const elapsed = Date.now() - new Date(dateStr).getTime();
  if (elapsed < HOUR_MS)
    return t("templates.minutes_ago", { count: Math.max(1, Math.floor(elapsed / MINUTE_MS)) });
  if (elapsed < DAY_MS) return t("templates.hours_ago", { count: Math.floor(elapsed / HOUR_MS) });
  const days = Math.floor(elapsed / DAY_MS);
  if (days === 1) return t("templates.yesterday");
  if (days < 7) return t("templates.days_ago", { count: days });
  if (days < 30) return t("templates.weeks_ago", { count: Math.round(days / 7) });
  return t("templates.months_ago", { count: Math.round(days / 30) });
};
